import type { ElementType } from 'react';
import { Sun, Moon, Monitor, Layout, PanelLeft, PanelRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import type { ThemeMode, SidebarMode, SidebarSide } from '@/components/settings/settings-storage';
import { cn } from '@/lib/utils';
import { COARSE_HIT_ALONE } from '@/lib/touch-target';

// #2109 R3-B4: labels are `settings:appearance.*` keys, resolved at render.
const themes = [
  { value: 'light', labelKey: 'appearance.theme.light', icon: Sun },
  { value: 'dark', labelKey: 'appearance.theme.dark', icon: Moon },
  { value: 'system', labelKey: 'appearance.theme.system', icon: Monitor },
] as const satisfies readonly { value: ThemeMode; labelKey: string; icon: ElementType }[];

const sidebarModes = [
  { value: 'auto', labelKey: 'appearance.display.modes.auto.label', descriptionKey: 'appearance.display.modes.auto.description' },
  { value: 'collapsed', labelKey: 'appearance.display.modes.collapsed.label', descriptionKey: 'appearance.display.modes.collapsed.description' },
  { value: 'hamburger', labelKey: 'appearance.display.modes.hamburger.label', descriptionKey: 'appearance.display.modes.hamburger.description' },
] as const satisfies readonly { value: SidebarMode; labelKey: string; descriptionKey: string }[];

interface AppearanceSectionProps {
  theme: ThemeMode;
  onThemeChange: (value: ThemeMode) => void;
  sidebarMode: SidebarMode;
  sidebarModeDirty: boolean;
  onSidebarModeChange: (value: SidebarMode) => void;
  onSidebarModeSave: () => void;
  sidebarSide: SidebarSide;
  onSidebarSideChange: (value: SidebarSide) => void;
}

export function AppearanceSection({
  theme,
  onThemeChange,
  sidebarMode,
  sidebarModeDirty,
  onSidebarModeChange,
  onSidebarModeSave,
  sidebarSide,
  onSidebarSideChange,
}: AppearanceSectionProps) {
  const { t } = useTranslation(['settings', 'common']);
  return (
    <>
      {/* ── Appearance ──────────────────────────────────── */}
      {/* Promoted to first section so the most-frequent preference (theme)
          is one scroll away. Companion section to the avatar-dropdown
          quick-toggle added in M2.8 — canonical control here, quick
          access there (Gmail/Notion/GitHub convention). */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sun className="h-5 w-5 text-primary" />
            <CardTitle>{t('appearance.theme.title')}</CardTitle>
          </div>
          <CardDescription>{t('appearance.theme.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {themes.map(({ value, labelKey, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => onThemeChange(value)}
                className={`flex flex-1 flex-col items-center gap-1.5 rounded-lg border px-3 py-3 text-caption transition-colors ${
                  theme === value
                    ? 'border-primary bg-primary/5 text-primary font-medium'
                    : 'border-border text-stevens-ink-3 hover:border-foreground/30 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t(labelKey)}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Display ─────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Layout className="h-5 w-5 text-primary" />
            <CardTitle>{t('appearance.display.title')}</CardTitle>
          </div>
          <CardDescription>{t('appearance.display.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Sidebar side — applies instantly. Helps one-handed reach on large
              phones (the drawer and its bottom-nav "More" trigger flip together).
              Mobile/small screens only; the desktop rail stays on the left. */}
          <div>
            <p className="text-body font-medium mb-1">{t('appearance.display.sideTitle')}</p>
            <p className="text-caption text-stevens-ink-3 mb-2">
              {t('appearance.display.sideDescription')}
            </p>
            <div className="flex gap-2">
              {([
                { value: 'left' as SidebarSide, labelKey: 'appearance.display.left' as const, icon: PanelLeft },
                { value: 'right' as SidebarSide, labelKey: 'appearance.display.right' as const, icon: PanelRight },
              ]).map(({ value, labelKey, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onSidebarSideChange(value)}
                  aria-pressed={sidebarSide === value}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-caption transition-colors ${
                    sidebarSide === value
                      ? 'border-primary bg-primary/5 text-primary font-medium'
                      : 'border-border text-stevens-ink-3 hover:border-foreground/30 hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t" />

          <RadioGroup
            value={sidebarMode}
            onValueChange={(v) => onSidebarModeChange(v as SidebarMode)}
          >
            {sidebarModes.map((mode) => (
              <div key={mode.value} className="flex items-start space-x-3 py-1">
                {/* #2603 (D-2420): the 16 px radio reaches 44 px on a coarse
                    pointer; the reach lies over its own label, which selects
                    the same option. */}
                <RadioGroupItem
                  value={mode.value}
                  id={`sidebar-${mode.value}`}
                  className={cn('mt-0.5', COARSE_HIT_ALONE, '[@media(pointer:coarse)]:after:-inset-3.5')}
                />
                <Label htmlFor={`sidebar-${mode.value}`} className="cursor-pointer">
                  <span className="block font-medium text-body">{t(mode.labelKey)}</span>
                  <span className="block text-caption text-stevens-ink-3">{t(mode.descriptionKey)}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
          <div className="flex justify-end pt-2">
            <Button onClick={onSidebarModeSave} disabled={!sidebarModeDirty} size="sm">
              {t('common:actions.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
